import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { CASCADE_LEAD_STATUS_LABEL_LIST } from '@command-center/leadloop-core';
import {
  LeadLoopLeadCard,
  LeadLoopLeadListItem,
  useLoadedRecordDetails,
  type RightSidebarRecordDetails,
} from '@command-center/oneup-portal-ui';
import { leadloopApi } from '../lib/leadloop-api';
import { usePagedView } from '../lib/use-paged-view';
import {
  Button,
  CollectionViewToggle,
  CompactRecordList,
  FilterSelect,
  ListToolbar,
  Pagination,
  RecordCardGrid,
  RecordTable,
  RecordTableCell,
  RecordTableRow,
  SearchField,
  WorkspaceSplitView,
  useCollectionView,
  useIsSplitView,
  usePageHeader,
} from '@command-center/ui-system';
import { LeadDetailView } from './LeadDetailView';
import { formatSourceLabel } from './LeadQuickView';
import { BulkActionBar, SelectionCheckbox, useBulkSelection } from '../components/bulk-record-actions';
import {
  Cell,
  DateCell,
  EmptyState,
  ErrorState,
  LeadStatusBadge,
  LoadingState,
  LoopReasonBadge,
  VerifiedTag,
} from './parts';

export function LeadsView() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('open');
  usePageHeader({ title: 'Leads', subtitle: 'Every opportunity, and what happens next.' }, []);
  const [view, setView] = useCollectionView('cascade:records:leads:view', 'list');
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { page, setPage, pageSize } = usePagedView([status, q]);
  const leads = useQuery({
    queryKey: ['leads', status, q, page, pageSize],
    queryFn: () => leadloopApi.leads({ status, q: q || undefined, page, pageSize }),
    placeholderData: (previous) => previous,
  });
  const loop = useQuery({ queryKey: ['loop'], queryFn: leadloopApi.loop });
  const loopByLeadId = useMemo(() => new Map((loop.data?.items ?? []).map((item) => [item.lead.id, item])), [loop.data?.items]);
  const items = useMemo(() => leads.data?.items ?? [], [leads.data?.items]);
  const selection = useBulkSelection(items.map((lead) => lead.id));
  const bulkDismiss = useMutation({
    mutationFn: () => leadloopApi.bulkDismissLeads(selection.selectedIds),
    onSuccess: () => {
      selection.clear();
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
      ]);
    },
  });
  const selectedIndex = id ? items.findIndex((lead) => lead.id === id) : -1;
  const detailPanel = useMemo(
    () => id ? (
      <LeadDetailView
        id={id}
        embedded
        onClose={() => navigate('/leads')}
        onPrevious={selectedIndex > 0 ? () => navigate(`/leads/${items[selectedIndex - 1]!.id}`) : undefined}
        onNext={selectedIndex >= 0 && selectedIndex < items.length - 1 ? () => navigate(`/leads/${items[selectedIndex + 1]!.id}`) : undefined}
      />
    ) : undefined,
    [id, items, navigate, selectedIndex],
  );
  const loadedRecord = useMemo<RightSidebarRecordDetails | undefined>(
    () => id && detailPanel ? {
      key: `lead:${id}`,
      recordId: id,
      recordTypeKey: 'lead',
      title: items[selectedIndex]?.title ?? id,
      content: detailPanel,
      sourcePath: '/leads',
      onClear: () => navigate('/leads'),
      canGoPrevious: selectedIndex > 0,
      canGoNext: selectedIndex >= 0 && selectedIndex < items.length - 1,
      onPrevious: () => navigate(`/leads/${items[selectedIndex - 1]!.id}`),
      onNext: () => navigate(`/leads/${items[selectedIndex + 1]!.id}`),
    } : undefined,
    [detailPanel, id, items, navigate, selectedIndex],
  );
  useLoadedRecordDetails(loadedRecord, Boolean(id));

  const pagination = (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={leads.data?.total}
      count={items.length}
      onPageChange={setPage}
      busy={leads.isFetching}
      label="leads"
    />
  );

  const total = leads.data?.total ?? items.length;
  // Below the split the detail replaces the list, so search and filters above it
  // would be operating on a list that is no longer on screen.
  const listVisible = useIsSplitView() || !id;

  return (
    <div className="flex flex-col gap-4">
      {listVisible && (
      <ListToolbar
        search={<SearchField value={q} onChange={setQ} placeholder="Search leads…" label="Search leads" />}
        filters={(
          /*
            Six options, not twelve. "Open" is the default and holds everything
            LeadLoop is still responsible for; the four lifecycle states are
            there for reading history — a handed-off lead from March, the ones
            that were dismissed. A retired label in a bookmarked URL still
            resolves server-side, so an old link keeps returning its records.
          */
          <FilterSelect
            value={status}
            onChange={setStatus}
            label="Filter by lifecycle"
            options={[
              { value: 'open', label: 'Open' },
              { value: 'all', label: 'All' },
              ...CASCADE_LEAD_STATUS_LABEL_LIST.map((label) => ({ value: label, label })),
            ]}
          />
        )}
        count={`${total} ${total === 1 ? 'lead' : 'leads'}`}
        viewToggle={!id && <CollectionViewToggle view={view} onChange={setView} />}
      />
      )}
      {!id && (
        <BulkActionBar
          count={selection.selectedIds.length}
          busy={bulkDismiss.isPending}
          onClear={selection.clear}
        >
          <Button
            size="sm"
            variant="outline"
            disabled={bulkDismiss.isPending}
            onClick={() => bulkDismiss.mutate()}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden /> Dismiss Leads
          </Button>
        </BulkActionBar>
      )}
      {bulkDismiss.error && (
        <p role="alert" className="text-sm text-destructive">
          {bulkDismiss.error instanceof Error ? bulkDismiss.error.message : 'The bulk action failed.'}
        </p>
      )}
      {leads.isLoading ? <LoadingState /> : leads.isError ? <ErrorState error={leads.error} /> : items.length === 0 ? <EmptyState label="No leads match." /> : (
        <WorkspaceSplitView>
          {id ? (
            <CompactRecordList title={`${leads.data?.total ?? items.length} leads`}>
              {items.map((lead) => (
                <LeadLoopLeadListItem
                  key={lead.id}
                  lead={lead}
                  reason={loopByLeadId.get(lead.id)?.reason}
                  selected={lead.id === id}
                  onSelect={() => navigate(`/leads/${lead.id}`)}
                />
              ))}
            </CompactRecordList>
          ) : view === 'cards' ? (
            <div className="flex flex-col gap-4">
              <RecordCardGrid>
                {/*
                  The same card the LeadLoop queue draws — literally the same
                  component now, shared with One Up — so a lead looks like itself
                  on every page in every portal. It replaced a label/value grid
                  whose four cells pushed the phone number and the customer's own
                  words off the card entirely.
                */}
                {items.map((lead) => (
                  <div key={lead.id} className="relative">
                    <span className="absolute left-3 top-3 z-10 rounded bg-background/90 p-1">
                      <SelectionCheckbox
                        checked={selection.selected.has(lead.id)}
                        onChange={() => selection.toggle(lead.id)}
                        label={`Select ${lead.title}`}
                      />
                    </span>
                    <LeadLoopLeadCard
                      lead={lead}
                      reason={loopByLeadId.get(lead.id)?.reason}
                      onOpen={() => navigate(`/leads/${lead.id}`)}
                      primaryAction={
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                        >
                          Open lead
                        </Button>
                      }
                    />
                  </div>
                ))}
              </RecordCardGrid>
              {pagination}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <RecordTable
                minWidth={900}
                columns={[
                  {
                    key: 'select',
                    header: (
                      <SelectionCheckbox
                        checked={selection.allSelected}
                        onChange={selection.toggleAll}
                        label="Select all Leads"
                      />
                    ),
                    className: 'w-10',
                  },
                  { key: 'lead', header: 'Lead' },
                  { key: 'contact', header: 'Contact / Organization' },
                  { key: 'status', header: 'Status' },
                  { key: 'source', header: 'Source', hideOnMobile: true },
                  { key: 'next', header: 'Next step', hideOnMobile: true },
                  { key: 'followup', header: 'Next follow-up', hideOnMobile: true },
                  { key: 'owner', header: 'Owner', hideOnMobile: true },
                  { key: 'activity', header: 'Last activity', hideOnMobile: true },
                ]}
              >
                {items.map((lead) => {
                  const loopItem = loopByLeadId.get(lead.id);
                  return (
                    <RecordTableRow
                      key={lead.id}
                      selected={selection.selected.has(lead.id)}
                      flagged={Boolean(loopItem)}
                      onOpen={() => navigate(`/leads/${lead.id}`)}
                    >
                      <RecordTableCell>
                        <SelectionCheckbox
                          checked={selection.selected.has(lead.id)}
                          onChange={() => selection.toggle(lead.id)}
                          label={`Select ${lead.title}`}
                        />
                      </RecordTableCell>
                      <RecordTableCell>
                        <div className="flex items-start gap-2">
                          <span className="block max-w-[200px] truncate font-medium">{lead.title}</span>
                          <LoopReasonBadge reason={loopItem?.reason} />
                        </div>
                      </RecordTableCell>
                      <RecordTableCell>
                        <Cell value={[lead.contactName, lead.companyName].filter(Boolean).join(' · ')} width="20ch" />
                      </RecordTableCell>
                      <RecordTableCell>
                        <span className="flex flex-wrap items-center gap-1.5">
                          <LeadStatusBadge status={lead.status} showActive />
                          <VerifiedTag verified={lead.verified} />
                        </span>
                      </RecordTableCell>
                      <RecordTableCell hideOnMobile><Cell value={formatSourceLabel(lead.source)} width="14ch" /></RecordTableCell>
                      <RecordTableCell hideOnMobile><Cell value={lead.nextStep} width="24ch" /></RecordTableCell>
                      <RecordTableCell hideOnMobile><DateCell value={lead.nextFollowUpAt} /></RecordTableCell>
                      <RecordTableCell hideOnMobile><Cell value={lead.assignedTo} width="14ch" /></RecordTableCell>
                      <RecordTableCell hideOnMobile><DateCell value={lead.updatedAt} /></RecordTableCell>
                    </RecordTableRow>
                  );
                })}
              </RecordTable>
              {pagination}
            </div>
          )}
        </WorkspaceSplitView>
      )}
    </div>
  );
}
